

import moment from "moment";
import { LoadingOutlined } from "@ant-design/icons";
import { FC, useEffect, useState } from "react";
import { Col, DatePicker, Input, Row, Select, Tooltip } from "antd";

import {
  ICountry,
  ICurrency,
  IJobGrade,
  ILongTermPlan,
  IModal,
  IPlanType,
} from "@types";

import {
  ALL_GRADES_VALUE,
  ALL_VALUE,
  showSuccessPopup,
  sortedByName,
} from "@utils";

import {
  installment_types,
  grant_types,
  vesting_types,
  grant_based_types,
  holding_periods,
} from "../config";

import {
  ErrorServices,
  useCreateLongTermPlanMutation,
  useFetchAllCompanyJobGradesQuery,
  useFetchCompanyCountriesQuery,
  useFetchCurrenciesQuery,
  useFetchLongTermPlanTypesQuery,
  useUpdateLongTermPlanMutation,
  useFetchAllCompanyJobFunctionsQuery,
} from "@services";

import { useTypedSelector, useFormValidation } from "@hooks";

import CustomModal from "@/components/customModel";

const { Option } = Select;

interface IAddLongTermPlan extends IModal {
  selectedLongTermPlan: ILongTermPlan | null;
  setSelectedLongTermPlan: React.Dispatch<
    React.SetStateAction<ILongTermPlan | null>
  >;
  isEdit?: boolean;
}

interface IFormValues {
  name: string;
  plan_type_id: number | null;
  currency_id: number | null;
  country_ids: number[];
  grade_ids: number[];
  holding_period: boolean;
  is_installment: boolean;
  is_amount: boolean;
  is_adhoc: boolean;
  date_of_grant: number | null;
  month_of_grant: number | null;
  installment_type: string;
  year_of_grant?: number | null;
  grant_type: string;
  vesting_type: string;
  grant_based_type: string;
  holding_period_no: number | null;

  job_function_id: number | null;
  sub_job_function_ids: number[];
}

const AddLongTermPlan: FC<IAddLongTermPlan> = ({
  isVisible,
  setIsVisible,
  selectedLongTermPlan,
  isEdit,
}) => {
  const [gradeCountries, setGradeCountries] =
    useState<ICountry[]>([]);

  const { company } = useTypedSelector(
    state => state.auth?.user!
  );

  const [longTermPlan, setLongTermPlan] =
    useState<IFormValues>({
      name: "",
      plan_type_id: null,
      currency_id: null,
      country_ids: [],
      grade_ids: [],
      holding_period: false,
      is_installment: false,
      is_amount: false,
      is_adhoc: false,
      date_of_grant: null,
      month_of_grant: null,
      installment_type: "",
      year_of_grant: null,
      grant_type: "",
      vesting_type: "",
      grant_based_type: "",
      holding_period_no: null,

      job_function_id: null,
      sub_job_function_ids: [],
    });

  const { id: company_id } = company!;

  const { data: planTypesData, isLoading: isFetchingTypes } =
    useFetchLongTermPlanTypesQuery(null);

  const { data: planTypes } = planTypesData || {};

  const {
    data: currenciesData,
    isLoading: isFetchingCurrencies,
  } = useFetchCurrenciesQuery(null);

  const { data: currencies } = currenciesData || {};

  const {
    data: jobGradesData,
    isLoading: isFetchingGrades,
  } = useFetchAllCompanyJobGradesQuery({
    company_id,
  });

  const { data: jobGrades } = jobGradesData || {};

  const {
    data: countriesData,
    isLoading: isFetchingCountries,
  } = useFetchCompanyCountriesQuery({
    company_id,
  });

  const { data: companyJobFunctionsData } =
    useFetchAllCompanyJobFunctionsQuery({
      companyId: company_id,
    });

  const jobFunctions = companyJobFunctionsData?.data || [];

  const subJobFunctions =
    jobFunctions.find(
      (item: any) =>
        item.id === longTermPlan.job_function_id
    )?.sub_job_functions || [];

  const { data: countries } = countriesData || {};

  const [createLongTermPlan, { isLoading: isCreating }] =
    useCreateLongTermPlanMutation();

  const [updateLongTermPlan, { isLoading: isUpdating }] =
    useUpdateLongTermPlanMutation();


  const handleGradeChange = (
    selectedGradeIds: number[]
  ) => {
    if (
      selectedGradeIds.includes(
        ALL_GRADES_VALUE as any
      )
    ) {
      const allGradeIds =
        jobGrades?.map(jg => jg.id) || [];

      const allCountries =
        jobGrades
          ?.flatMap(jg => jg.countries || [])
          .filter(
            (c, idx, self) =>
              c &&
              idx ===
              self.findIndex(
                sc => sc.id === c.id
              )
          ) || [];

      setGradeCountries(allCountries);

      setLongTermPlan(prev => ({
        ...prev,
        grade_ids: allGradeIds,
        country_ids: [],
      }));

      return;
    }

    const selectedJobGrades =
      jobGrades?.filter(jg =>
        selectedGradeIds.includes(jg.id)
      ) || [];

    const mergedCountries =
      selectedJobGrades.flatMap(
        jg => jg.countries || []
      ) || [];

    const uniqueCountries = mergedCountries.filter(
      (c, idx, self) =>
        c &&
        idx ===
        self.findIndex(sc => sc.id === c.id)
    );

    setGradeCountries(uniqueCountries);

    setLongTermPlan(prev => ({
      ...prev,
      grade_ids: selectedGradeIds,
      country_ids: [],
    }));
  };

  const handleGradeChangeForEdit = (
    selectedGradeIds: number[]
  ) => {
    const selectedJobGrades =
      jobGrades?.filter(jg =>
        selectedGradeIds?.includes(jg.id)
      ) || [];

    const mergedCountries =
      selectedJobGrades.flatMap(
        jg => jg.countries || []
      ) || [];

    const uniqueCountries = mergedCountries.filter(
      (c, idx, self) =>
        c &&
        idx ===
        self.findIndex(sc => sc.id === c.id)
    );

    setGradeCountries(uniqueCountries);

    setLongTermPlan(prev => ({
      ...prev,
      grade_ids: selectedGradeIds,
    }));
  };

  useEffect(() => {
    if (selectedLongTermPlan && isEdit) {
      const {
        countries,
        currency,
        holding_period,
        type,
        is_installment,
        is_amount,
        is_adhoc,
      } = selectedLongTermPlan;

      const normalizedCountries =
        countries?.map((c: any) => ({
          id: c.id,
          name: c?.name,
        })) || [];

      const longTermPlan: any = {
        ...selectedLongTermPlan,

        country_ids:
          countries?.map(({ id }) => id) || [],

        currency_id: currency?.id,

        holding_period: !!holding_period,

        plan_type_id: type?.id,

        year_of_grant: moment().year(),

        vesting_type: is_installment
          ? "INSTALLMENT"
          : "CLIFF",

        grant_based_type: is_amount
          ? "VALUE"
          : "UNITS",

        grant_type: is_adhoc
          ? "ADHOC"
          : "ANNUAL",

        grade_ids:
          selectedLongTermPlan?.grade_obj?.length
            ? selectedLongTermPlan.grade_obj.map(
              (grade: any) => grade.id
            )
            : (jobGrades || [])
              .filter((jg: any) =>
                selectedLongTermPlan?.grades?.includes(
                  jg.grade
                )
              )
              .map((jg: any) => jg.id),

        job_function_id:
          selectedLongTermPlan?.job_function?.[0]?.id ||
          selectedLongTermPlan?.job_function?.id ||
          selectedLongTermPlan?.job_function_id ||
          null,

        sub_job_function_ids:
          selectedLongTermPlan?.job_sub_function?.length
            ? selectedLongTermPlan.job_sub_function.map((item: any) => item.id)
            : selectedLongTermPlan?.sub_job_function?.length
              ? selectedLongTermPlan.sub_job_function.map((item: any) => item.id)
              : selectedLongTermPlan?.sub_job_function?.id
                ? [selectedLongTermPlan.sub_job_function.id]
                : selectedLongTermPlan?.sub_job_function_id
                  ? [selectedLongTermPlan.sub_job_function_id]
                  : [],
      };

      delete longTermPlan?.countries;
      delete longTermPlan?.currency;
      delete longTermPlan?.id;
      delete longTermPlan?.is_global;
      delete longTermPlan?.is_all_grade;
      delete longTermPlan?.type;
      delete longTermPlan?.grade_obj;

      setLongTermPlan(longTermPlan);

      handleGradeChangeForEdit(
        longTermPlan?.grade_ids
      );

      if (normalizedCountries?.length > 0) {
        const countryIds =
          normalizedCountries?.map(c => c?.id);

        setLongTermPlan(prev => ({
          ...prev,
          country_ids: countryIds,
        }));
      }
    } else {
      setLongTermPlan({
        name: "",
        plan_type_id: null,
        currency_id: null,
        country_ids: [],
        grade_ids: [],
        holding_period: false,
        is_installment: false,
        is_amount: false,
        is_adhoc: false,
        date_of_grant: null,
        month_of_grant: null,
        installment_type: "",
        year_of_grant: null,
        grant_type: "",
        vesting_type: "",
        grant_based_type: "",
        holding_period_no: null,
      });

      setGradeCountries([]);
    }
  }, [
    selectedLongTermPlan,
    isEdit,
    jobGrades,
  ]);

  const handleSubmit = async () => {
    try {
      const body = {
        ...longTermPlan,

        month_of_grant:
          longTermPlan?.month_of_grant || null,

        date_of_grant:
          longTermPlan?.date_of_grant || null,

        installment_type:
          longTermPlan?.vesting_type ===
            "CLIFF"
            ? null
            : longTermPlan?.installment_type,

        country_ids:
          longTermPlan?.country_ids,

        grade_ids:
          longTermPlan?.grade_ids,
      };

      delete body.year_of_grant;

      if (selectedLongTermPlan && isEdit) {
        await editShortTermPlan(body);
      } else {
        await addLongTermPlan(body);
      }

      setIsVisible(false);

      showSuccessPopup({
        title: `Long Term Incentive Plan ${isEdit ? "Updated!" : "Created!"
          }`,
        desc: `You have successfully ${isEdit
          ? "updated the"
          : "created a new"
          } LTI plan.`,
        role: "client",
      });
    } catch (error) {
      ErrorServices(error);
    }
  };

  const addLongTermPlan = async (
    body: IFormValues
  ) =>
    await createLongTermPlan({
      company_id,
      body,
    }).unwrap();

  const editShortTermPlan = async (
    body: IFormValues
  ) =>
    await updateLongTermPlan({
      company_id,
      id: selectedLongTermPlan?.id,
      body,
    }).unwrap();

  const validation = useFormValidation(
    longTermPlan,
    v => ({
      name: !v.name,
      plan_type_id: !v.plan_type_id,
      currency_id: !v.currency_id,

      grade_ids:
        !v.grade_ids ||
        v.grade_ids.length === 0,

      countries:
        !v.country_ids ||
        v.country_ids.length === 0,

      vesting_type: !v.vesting_type,

      grant_based_type:
        !v.grant_based_type,

      installment_type:
        v.vesting_type ===
        "INSTALLMENT" &&
        !v.installment_type,

      // job_function_id: !v.job_function_id,
      // sub_job_function_ids:
      //   !v.sub_job_function_ids ||
      //   v.sub_job_function_ids.length === 0,
    })
  );

  const missingFields = [
    !longTermPlan.name && "Plan Name",
    !longTermPlan.plan_type_id && "Type of Stock",
    !longTermPlan.currency_id && "Currency",
    (!longTermPlan.grade_ids || longTermPlan.grade_ids.length === 0) &&
    "Grades",
    (!longTermPlan.country_ids || longTermPlan.country_ids.length === 0) &&
    "Countries",
    !longTermPlan.vesting_type && "Vesting Type",
    !longTermPlan.grant_based_type && "Grant Based Type",
    longTermPlan.vesting_type === "INSTALLMENT" &&
    !longTermPlan.installment_type &&
    "Installment Schedule",
  ].filter(Boolean);

  const disabledReason =
    missingFields.length > 0
      ? `Please fill in: ${missingFields.join(", ")}`
      : isCreating || isUpdating
        ? "Please wait..."
        : "";

  return (
    <CustomModal
      width="w-8/12"
      height="650px"
      footer={
        <>
          <button
            className="bg-revampOrange text-white font-medium text-lg py-2 px-14 rounded-md"
            onClick={() => setIsVisible(false)}
          >
            Cancel
          </button>
          <Tooltip title={disabledReason}>
            <span>
              <button
                className={`bg-revampBlue text-white font-medium text-lg py-2 px-14 rounded-md ${!validation.isValid || isCreating || isUpdating
                    ? "opacity-50 cursor-not-allowed"
                    : ""
                  }`}
                disabled={
                  !validation.isValid ||
                  isCreating ||
                  isUpdating
                }
                onClick={handleSubmit}
              >
                {isCreating || isUpdating ? (
                  <LoadingOutlined className="spinner" />
                ) : isEdit ? (
                  "Update"
                ) : (
                  "Create"
                )}
              </button>
            </span>
          </Tooltip>
        </>
      }
      title={`${isEdit ? "Update" : "Create"} LTI Plan`}
      isVisible={isVisible}
    >
      <>
        <Row justify="space-between">
          <Col span={11}>
            <label className="font-medium text-revampBlue text-lg leading-10 mt-4 ">
              <span className="font-medium text-revampBlue text-lg">
                Plan Name <span className="text-red-500">*</span>
              </span>
            </label>
            <Input
              className={`borderless-input w-full shadow-sm my-2 font-semibold text-sm bg-white px-3 py-3 rounded-md`}
              value={longTermPlan?.name}
              onBlur={() => validation.touch("name")}
              onChange={event =>
                setLongTermPlan(prev => ({
                  ...prev,
                  name: event?.target.value,
                }))
              }
              size="large"
              placeholder="Enter plan name here..."
            />
          </Col>
          <Col span={11}>
            <label className="font-medium text-revampBlue text-lg leading-10 mt-6 ">
              <span className="font-medium text-revampBlue text-lg">
                Vesting Type <span className="text-red-500">*</span>
              </span>
            </label>
            <Select
              size="large"
              className="custom-select"
              showArrow={true}
              showSearch={false}
              value={longTermPlan?.vesting_type || undefined}
              onBlur={() => validation.touch("vesting_type")}
              onChange={vesting_type => {
                if (vesting_type == "INSTALLMENT") {
                  setLongTermPlan(prev => ({ ...prev, is_installment: true }));
                } else {
                  setLongTermPlan(prev => ({ ...prev, is_installment: false }));
                }
                setLongTermPlan(prev => ({ ...prev, vesting_type }));
              }}
              placeholder="Select installment type from here..."
            >
              {vesting_types.map(type => (
                <Option key={type.value} value={type.value}>
                  {type.label}
                </Option>
              ))}
            </Select>
          </Col>
        </Row>



        <Row justify="space-between">
          <Col span={11}>
            <label className="font-medium text-revampBlue text-lg leading-10 mt-4 ">
              Holding Period
            </label>
            <Select
              size="large"
              className="custom-select"
              showArrow={true}
              showSearch={false}
              value={longTermPlan?.holding_period_no || undefined}
              // onBlur={() => validation.touch("holding_period_no")}
              onChange={count => {
                setLongTermPlan(prev => ({
                  ...prev,
                  holding_period_no: count,
                  holding_period: true,
                }));
              }}
              placeholder="Select grant based type from here..."
            >
              {holding_periods.map(type => (
                <Option key={type.value} value={type.value}>
                  {type.label}
                </Option>
              ))}
            </Select>
          </Col>
          <Col span={11}>
            <label className="font-medium text-revampBlue text-lg leading-10 mt-4 ">

              <span className="font-medium text-revampBlue text-lg">
                Installment Schedule
                {!(longTermPlan?.vesting_type == "CLIFF") && <span className="text-red-500 ml-1">*</span>}
              </span>
            </label>
            <Select
              size="large"
              className="custom-select"
              showArrow={true}
              showSearch={false}
              disabled={longTermPlan?.vesting_type == "CLIFF"}
              value={longTermPlan?.installment_type || undefined}
              onBlur={() => validation.touch("installment_type")}
              onChange={installment_type =>
                setLongTermPlan(prev => ({ ...prev, installment_type }))
              }
              placeholder="Select installment type from here..."
            >
              {installment_types.map(type => (
                <Option key={type.value} value={type.value}>
                  {type.label}
                </Option>
              ))}
            </Select>
          </Col>
        </Row>
        <Row justify="space-between">
          <Col span={11}>
            <label className="font-medium text-revampBlue text-lg leading-10 mt-4 ">
              <span className="font-medium text-revampBlue text-lg">
                Type of Stock <span className="text-red-500">*</span>
              </span>
            </label>
            <Select
              loading={isFetchingTypes}
              size="large"
              className="custom-select"
              showArrow={true}
              showSearch={false}
              value={longTermPlan?.plan_type_id || undefined}
              onBlur={() => validation.touch("plan_type_id")}
              onChange={plan_type_id =>
                setLongTermPlan(prev => ({ ...prev, plan_type_id }))
              }
              placeholder="Select stock type from here..."
            >
              {sortedByName(planTypes)?.map((type: IPlanType) => (
                <Option key={type.id} value={type.id}>
                  {type.name}
                </Option>
              ))}
            </Select>
          </Col>
          <Col span={11}>
            <label className="font-medium text-revampBlue text-lg leading-10 mt-4 ">
              <span className="font-medium text-revampBlue text-lg">
                Currency <span className="text-red-500">*</span>
              </span>
            </label>
            <Select
              className="custom-select"
              showArrow={true}
              showSearch={true}
              loading={isFetchingCurrencies}
              filterOption={(input: any, option: any) =>
                option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
              }
              value={longTermPlan?.currency_id || undefined}
              onBlur={() => validation.touch("currency_id")}
              onChange={currency_id =>
                setLongTermPlan(prev => ({
                  ...prev,
                  currency_id,
                }))
              }
              size="large"
              placeholder="Select currency from here..."
            >
              {sortedByName(currencies)?.map(
                ({ id, name, code }: ICurrency) => (
                  <Option key={id} value={id}>
                    {`${name} (${code})`}
                  </Option>
                )
              )}
            </Select>
          </Col>
        </Row>
        <Row justify="space-between">
          <Col span={11}>
            <label className="font-medium text-revampBlue text-lg leading-10 mt-4 ">
              <span className="font-medium text-revampBlue text-lg">
                Grades <span className="text-red-500">*</span>
              </span>
            </label>
            <Select
              className="custom-select"
              loading={isFetchingGrades}
              mode="multiple"
              maxTagCount="responsive"
              maxTagPlaceholder={(omittedValues) => `+${omittedValues.length} more`}
              showArrow
              size="large"
              showSearch={true}
              placeholder="Select grades from here..."
              optionLabelProp="label"
              value={longTermPlan?.grade_ids}
              onBlur={() => validation.touch("grade_ids")}
              onChange={handleGradeChange}
            >


              {/* All Grades option (only show if not all selected) */}
              {!jobGrades
                ?.map(jgd => jgd.id)
                ?.every(id =>
                  longTermPlan?.grade_ids?.includes(id)
                ) && (
                  <Option
                    key={ALL_GRADES_VALUE}
                    value={ALL_GRADES_VALUE}
                  >
                    All Grades
                  </Option>
                )}

              {jobGrades?.map((jobGrade: IJobGrade) => (
                <Option
                  key={jobGrade.id}
                  value={jobGrade.id}
                  label={`${jobGrade.grade}`}

                >
                  {jobGrade?.grade}
                </Option>
              ))}
            </Select>
          </Col>

          <Col span={11}>
            <label className="font-medium text-revampBlue text-lg leading-10 mt-4 ">
              <span className="font-medium text-revampBlue text-lg">
                Countries <span className="text-red-500">*</span>
              </span>
            </label>
            <Select
              className="custom-select"
              filterOption={(input: any, option: any) =>
                option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
              }
              loading={isFetchingCountries}
              showArrow={true}
              showSearch={true}
              disabled={
                longTermPlan?.grade_ids?.length === 0
              }
              mode="multiple"
              maxTagCount="responsive"
              maxTagPlaceholder={(omittedValues) => `+${omittedValues.length} more`}
              size="large"
              onBlur={() => validation.touch("countries")}
              onChange={(country_ids: any[]) => {
                if (country_ids.includes(ALL_VALUE)) {
                  setLongTermPlan(prev => ({
                    ...prev,
                    country_ids: gradeCountries?.map(c => c.id) || [],
                  }));
                } else {
                  setLongTermPlan(prev => ({
                    ...prev,
                    country_ids,
                  }));
                }
              }}
              value={longTermPlan?.country_ids} // If Global is selected, show "All", otherwise show selected countries
              placeholder="Select countries from here..."
            >
              {!gradeCountries?.every(c =>
                longTermPlan?.country_ids?.includes(c.id)
              ) && (
                  <Option key={ALL_VALUE} value={ALL_VALUE}>
                    All Countries
                  </Option>
                )}
              {/* Render country options */}
              {sortedByName(gradeCountries)?.map(({ id, name }: ICountry) => (
                <Option key={id} value={id}>
                  {name}
                </Option>
              ))}
            </Select>
          </Col>
        </Row>

        <Row justify="space-between">
          <Col span={11}>
            <label className="font-medium text-revampBlue text-lg leading-10 mt-4">
              Function
            </label>

            <Select
              className="custom-select"
              size="large"
              placeholder="Select Function..."
              value={longTermPlan.job_function_id || undefined}
              onChange={job_function_id =>
                setLongTermPlan(prev => ({
                  ...prev,
                  job_function_id,
                  sub_job_function_ids: [],
                }))
              }
            >
              {jobFunctions.map((item: any) => (
                <Option key={item.id} value={item.id}>
                  {item.name}
                </Option>
              ))}
            </Select>
          </Col>

          <Col span={11}>
            <label className="font-medium text-revampBlue text-lg leading-10 mt-4">
              Sub-Function
            </label>

            <Select
              mode="multiple"
              maxTagCount="responsive"
              maxTagPlaceholder={(omittedValues) => `+${omittedValues.length} more`}
              className="custom-select"
              size="large"
              placeholder="Select Sub-Functions..."
              value={longTermPlan.sub_job_function_ids}
              disabled={!longTermPlan.job_function_id}
              onChange={(value: any[]) => {
                const allSubFunctionIds = subJobFunctions.map(
                  (item: any) => item.id
                );

                if (value.includes("ALL_SUB_FUNCTIONS")) {
                  setLongTermPlan(prev => ({
                    ...prev,
                    sub_job_function_ids: allSubFunctionIds,
                    all_plan_sub_functions: true,
                  }));

                  return;
                }

                setLongTermPlan(prev => ({
                  ...prev,
                  sub_job_function_ids: value,
                  all_plan_sub_functions: false,
                }));
              }}
            >
              <Option
                key="ALL_SUB_FUNCTIONS"
                value="ALL_SUB_FUNCTIONS"
              >
                All Sub Functions
              </Option>

              {subJobFunctions.map((item: any) => (
                <Option key={item.id} value={item.id}>
                  {item.name}
                </Option>
              ))}
            </Select>
          </Col>
        </Row>

        <Row justify="space-between">
          <Col span={11}>
            <label className="font-medium text-revampBlue text-lg leading-10 mt-4 ">
              Grant Type
            </label>
            <Select
              size="large"
              className="custom-select"
              showArrow={true}
              showSearch={false}
              value={longTermPlan?.grant_type || undefined}
              // onBlur={() => validation.touch("grant_type")}
              onChange={grant_type => {
                if (grant_type == "ADHOC") {
                  setLongTermPlan(prev => ({ ...prev, is_adhoc: true }));
                } else {
                  setLongTermPlan(prev => ({ ...prev, is_adhoc: false }));
                }
                setLongTermPlan(prev => ({ ...prev, grant_type }));
              }}
              placeholder="Select grant type from here..."
            >
              {grant_types.map(type => (
                <Option key={type.value} value={type.value}>
                  {type.label}
                </Option>
              ))}
            </Select>
          </Col>
          <Col span={11}>
            <label className="font-medium text-revampBlue text-lg leading-10 mt-4">
              Date of Grant
            </label>
            <br />
            <DatePicker
              className={`borderless-input w-full shadow-sm font-semibold text-sm bg-white px-3 py-3 rounded-md`}
              size="large"
              disabled={longTermPlan?.grant_type === "ADHOC"}
              picker="date"
              value={
                longTermPlan?.month_of_grant && longTermPlan?.date_of_grant
                  ? moment(
                    `${new Date().getFullYear()}-${longTermPlan.month_of_grant
                    }-${longTermPlan.date_of_grant}`,
                    "YYYY-MM-DD"
                  )
                  : undefined
              }
              onChange={date => {
                if (date) {
                  setLongTermPlan(prev => ({
                    ...prev,
                    month_of_grant: moment(date).month() + 1, // Update month (1-based index)
                    date_of_grant: moment(date).date(), // Update day
                  }));
                }
              }}
              placeholder="Select date of grant from here..."
              format="MM/DD" // Show only the month and day format
            />
          </Col>
        </Row>

        <Row justify="space-between">
          <Col span={11}>
            <label className="font-medium text-revampBlue text-lg leading-10 mt-4 ">
              <span className="font-medium text-revampBlue text-lg">
                Grant Based Type <span className="text-red-500">*</span>
              </span>
            </label>
            <Select
              size="large"
              className="custom-select"
              showArrow={true}
              showSearch={false}
              value={longTermPlan?.grant_based_type || undefined}
              onBlur={() => validation.touch("grant_based_type")}
              onChange={grant_based_type => {
                if (grant_based_type === "VALUE") {
                  setLongTermPlan(prev => ({ ...prev, is_amount: true }));
                }
                if (grant_based_type === "UNITS") {
                  setLongTermPlan(prev => ({ ...prev, is_amount: false }));
                }
                setLongTermPlan(prev => ({ ...prev, grant_based_type }));
              }}
              placeholder="Select grant based type from here..."
            >
              {grant_based_types.map(type => (
                <Option key={type.value} value={type.value}>
                  {type.label}
                </Option>
              ))}
            </Select>
          </Col>
        </Row>

        {/* <Row justify="space-between" className="my-6">
          <Col className="align-center-vertically" span={24}>
            <Checkbox
              checked={global}
              onChange={e => setGlobal(e.target.checked)}
            >
              <span className={"font-medium text-lg text-themeInputTitleColor"}>
                Global
              </span>
            </Checkbox>
            <Checkbox
              checked={longTermPlan?.holding_period}
              onChange={e =>
                setLongTermPlan(prev => ({
                  ...prev,
                  holding_period: e.target.checked,
                }))
              }
            >
              <span className={"font-medium text-lg text-themeInputTitleColor"}>
                Period
              </span>
            </Checkbox>
            <Checkbox
              checked={longTermPlan?.is_installment}
              onChange={e =>
                setLongTermPlan(prev => ({
                  ...prev,
                  is_installment: e.target.checked,
                }))
              }
            >
              <span className={"font-medium text-lg text-themeInputTitleColor"}>
                Installment
              </span>
            </Checkbox>
            <Checkbox
              checked={longTermPlan?.is_amount}
              onChange={e =>
                setLongTermPlan(prev => ({
                  ...prev,
                  is_amount: e.target.checked,
                }))
              }
            >
              <span className={"font-medium text-lg text-themeInputTitleColor"}>
                Amount
              </span>
            </Checkbox>
            <Checkbox
              checked={longTermPlan?.is_adhoc}
              onChange={e =>
                setLongTermPlan(prev => ({
                  ...prev,
                  is_adhoc: e.target.checked,
                }))
              }
            >
              <span className={"font-medium text-lg text-themeInputTitleColor"}>
                Adhoc
              </span>
            </Checkbox>
          </Col>
        </Row> */}
      </>
    </CustomModal>
  );
};

export default AddLongTermPlan;
